import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useSettingsStore } from "@editor/store/settings.store";

import type { FunctionName } from "./expressionFunctions";

import {
  variables as variablesList,
  functions as functionsList
} from "./expressionParts";

export function useExpressionMenu() {
  const { t } = useI18n();

  const settings = useSettingsStore();

  const variables = computed(() =>
    Object.values(variablesList).map((v) => ({
      code: v.code,
      title: t(`expressionVariables.${v.code}`),
      available: v.unitsWhereAvailable.has(settings.simulationRoundUnit)
    }))
  );

  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const indexStore = useIndexStore();

  const targets = computed(() => targetStore.orderedItems);
  const resources = computed(() => resourceStore.orderedItems);
  const indices = computed(() => indexStore.orderedItems);

  const elements = computed(() => {
    return [
      ...targets.value,
      ...resources.value,
      ...indices.value
    ];
  });

  const functions = computed(() => {
    const annotatedFunctions: Record<
      string,
      {
        code: FunctionName;
        title: string;
        args: number;
        connectors?: string[];
        defaultArgs: (number | string)[];
      }
    > = {};

    for (const [name, func] of Object.entries(functionsList)) {
      annotatedFunctions[name] = {
        code: name as FunctionName,
        title: t("expressionFunctions." + name),
        args: func.args,
        connectors: func.connectors,
        defaultArgs: [...func.defaultArgs]
      };
    }

    return Object.values(annotatedFunctions);
  });

  const baseOperators = computed(() => {
    return [
      { code: "+" },
      { code: "-" },
      { code: "*" },
      { code: "/" }
    ] as const;
  });

  const parentheses = computed(() => {
    return [
      { code: "opening", title: "(" },
      { code: "closing", title: ")" }
    ] as const;
  });

  const comparisonOperators = computed(() => {
    return [
      { code: ">" },
      { code: "<" },
      { code: ">=" },
      { code: "<=" },
      { code: "==" },
      { code: "!=" }
    ] as const;
  });

  return {
    variables,
    elements,
    targets,
    resources,
    indices,
    functions,
    baseOperators,
    parentheses,
    comparisonOperators
  };
}
